import { Middleware, MiddlewareAPI } from 'redux';

import { RegexConst, urlMap } from '@nocowanie/core/consts';

import { LocationType, LocationTypeEnum, SliceNameEnum } from '@app/enums';
import { filterUnsupportedFilters } from '@app/helpers/listing.helpers';
import { getListingSlugFromUrl } from '@app/helpers/listing-mapping.helpers';
import { mapSearchStateToUrlParams } from '@app/helpers/search-params.helpers';
import { ListingGeoPrefix } from '@app/models';
import {
    setPageAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
} from '@app/state/slices/search-state.actions';

// Rewrites pretty url (with categories, amenities etc) with basic geography url
function rewritePrettyUrl(url: URL, locationType: LocationType) {
    const [prefix, ...details] = url.pathname.split('/').filter(part => part.length);
    const geoSlug = getListingSlugFromUrl(prefix, details);
    const pathnames = {
        [ListingGeoPrefix.Regiony]: `/${ListingGeoPrefix.Regiony}/${geoSlug}/`,
        [ListingGeoPrefix.Noclegi]: `/${ListingGeoPrefix.Noclegi}/${geoSlug}/`,
    };

    if (
        locationType === LocationTypeEnum.Region &&
        url.pathname !== pathnames[ListingGeoPrefix.Regiony]
    ) {
        url.pathname = pathnames[ListingGeoPrefix.Regiony];
    } else if (
        [LocationTypeEnum.City, LocationTypeEnum.Voivodeship].includes(
            locationType as LocationTypeEnum,
        ) &&
        url.pathname !== pathnames[ListingGeoPrefix.Noclegi]
    ) {
        url.pathname = pathnames[ListingGeoPrefix.Noclegi];
    }
}

// Checks if action is a search action with shouldUpdateUrl flag
function isSearchUpdateUrlAction(action: unknown): boolean {
    if (!action || typeof action !== 'object' || !('type' in action) || !('payload' in action)) {
        return false;
    }

    const { type, payload } = action;

    const isSearchAction =
        type === updateSearchCriteriaAction.type ||
        type === updateSearchFiltersAction.type ||
        type === setPageAction.type;

    const hasShouldUpdateUrlFlag =
        payload &&
        typeof payload === 'object' &&
        payload !== null &&
        'shouldUpdateUrl' in payload &&
        payload.shouldUpdateUrl === true;

    return Boolean(isSearchAction && hasShouldUpdateUrlFlag);
}

/**
 * Middleware to sync map search state with URL parameters.
 * Only updates the URL when actions include shouldUpdateUrl=true flag.
 */
export const searchUrlMiddleware: Middleware =
    (api: MiddlewareAPI) => next => (action: unknown) => {
        const result = next(action);

        if (typeof window === 'undefined') {
            return result;
        }

        if (isSearchUpdateUrlAction(action)) {
            const state = api.getState();
            const searchState = state[SliceNameEnum.Search];
            const { searchCriteria, filters, page } = searchState;

            const searchParams = mapSearchStateToUrlParams(
                searchCriteria,
                filterUnsupportedFilters(filters),
                page,
            );

            // On location listing pages the route path already encodes the location,
            // so these params are redundant in the query string.
            const isCampaignListingPage = RegexConst.CAMPAIGN_PAGE_TYPE.test(
                window.location.pathname,
            );

            const url = new URL(window.location.href);
            const isMap = url.pathname === `${urlMap.home}${urlMap.map}`;

            if (!isCampaignListingPage && !isMap) {
                searchParams.delete('location');
                searchParams.delete('geoId');
                searchParams.delete('type');
            }

            url.search = searchParams.toString();

            if (!isMap) {
                rewritePrettyUrl(url, searchCriteria.locationType);
            }

            window.history.replaceState({}, '', url.toString());
        }

        return result;
    };
